import { pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateObject } from './create-object'
import { CreateTuple } from './create-tuple'
import { CommonName, UnsupportedNodeError } from './utils'

// TODO: string1 = 'key', string2 = 'value'
/**
 * TODO ({ key: string; value: any }[] -> object)
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface ListToObject<out T = readonly any[]> {
  // TODO: maybe use more specific list type for ListToObject and ListToObject
  // (e.g. { key: string; value: any }[])
  /** {@link ListToObject} */
  readonly [Key.ListToObject]: T extends Object.Constraint
    ?
        | Expression<readonly any[]>
        | readonly [
            listExpression: Expression<readonly any[]>,
            keyProp?: string,
            valueProp?: string,
          ]
    : never
}
export namespace ListToObject {
  export const isListToObject: TypeChecker<ListToObject> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<ListToObject>({
        [Key.ListToObject]: TypeChecker.isAny, // TODO:
      }),
    ).usingAlias('Fn.ListToObject')
  export const assertIsListToObject: TypeAsserter<ListToObject> =
    TypeAsserter.Factory.from(isListToObject)

  /**
   * @see {@link Node}
   */
  export class Node<T extends number = number> extends fnNode.Node<
    T,
    Key.ListToObject,
    {
      readonly list: fnNode.Node<readonly unknown[]>
      /**
       * Entries in the resulting list should have a property where this is the
       * key and the value is the __key__ for that entry
       */
      readonly keyProp: string
      /**
       * Entries in the resulting list should have a property where this is the
       * key and the value is the __value__ for that entry
       */
      readonly valueProp: string
      /**
       * In some cases, it is possible to determine all of the properties ahead
       * of time, but with some of those properties having keys that are
       * {@link data.expression.fn.node.Node}. In those cases, we can't use {@link CreateObject}, so
       * we store the properties here, in case they can be handled more
       * efficiently than using {@link list}, {@link keyProp}, and
       * {@link valueProp}
       */
      // NOTE: I am not sure if this is the best way to do this, but it seemed
      // there was some value in leaving CreateObject's arg as a mapped type on
      // T, so I decided to just do this here (on 5/17/2024). It may be worth
      // reinvestigating that decision as the sytem evolves
      readonly properties?: readonly {
        readonly key: expressionNode.Node<string>
        readonly value: expressionNode.Node<any>
      }[]
    }
  > {
    public readonly type = common.value.types.Intrinsic.Object

    constructor(arg: Node<T>['arg']) {
      super(Key.ListToObject, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<ListToObject> = {
    createNode: (fn, context) => {
      const [
        listExpression,
        keyProp = CommonName.Key,
        valueProp = CommonName.Value,
      ] = pack(fn[Key.ListToObject])
      const list = expressionNode.from(listExpression, context)
      if (expressionNode.ForValue.isForValue(list)) {
        // If list is a value, we can convert to object and return the resulting
        // value
        return new expressionNode.ForValue(
          // TODO: handle if value of list is not an array
          (
            list.value as (Record<typeof keyProp, string> &
              Record<typeof valueProp, unknown>)[]
          ).reduce<Record<string, unknown>>((prev, entry) => {
            prev[entry[keyProp]] = entry[valueProp]
            return prev
          }, {}),
        )
      }
      /**
       * @see {@link Node}
       */
      let properties: Mutable<Node['arg']['properties']>
      if (list instanceof CreateTuple.Node) {
        // If the list that we are converting to object is from CreateTuple, we
        // _might_ be able to use either properties or CreateObject
        properties = []
        let objectExpressions: Mutable<CreateObject.Node['arg']> | undefined =
          {}
        for (const item of list.arg) {
          if (expressionNode.ForValue.isForValue(item)) {
            // If the item is a value node, then we can easily extract the key
            // and value to use in setting the property for both properties or
            // objectExpressions
            const { [keyProp]: key, [valueProp]: value } =
              (item.value as Unknown) ?? {}
            const keyStr = key?.toString()
            if (keyStr !== undefined) {
              // We can only add the property if the key is defined
              const valueNode = new expressionNode.ForValue(value)
              properties.push({
                key: new expressionNode.ForValue(keyStr),
                value: valueNode,
              })
              if (objectExpressions) {
                objectExpressions[keyStr] = valueNode
              }
            }
          }
          if (item instanceof CreateObject.Node) {
            // If the item is a CreateObject node...
            const { [keyProp]: keyNode, [valueProp]: valueNode } = item.arg
            // Then we can add the property to properties...
            properties.push({ key: keyNode, value: valueNode })
            if (expressionNode.ForValue.isForValue(keyNode)) {
              const keyStr = keyNode.value?.toString()
              // And, if the key is a value node with a non-nullish value, then
              // we can also set the property in objectExpressions, if it exists
              if (objectExpressions && keyStr !== undefined) {
                objectExpressions[keyStr] = valueNode
              }
            } else {
              // Otherwise, we have to give up on using CreateObject since this
              // property can't be added to objectExpressions
              objectExpressions = undefined
            }
          } else {
            // If we get to this point, we are unable to get the property from
            // this item, so we can't use properties or CreateObject
            properties = undefined
            objectExpressions = undefined
            break
          }
        }
        if (objectExpressions) {
          // Minor note: we know that at least one of the expressions in
          // objectExpressions is not a value expression, as if they were all
          // value expressions, the first if block above would have handled that
          // case. So we don't need to worry about potentially creating a value
          // node here
          return new CreateObject.Node(objectExpressions)
        }
      }
      if (list instanceof fnNode.Node) {
        return new Node({ keyProp, list, properties, valueProp })
      }
      // If we get to this point, we're not sure what else to do
      throw new UnsupportedNodeError()
    },
    expressionsInArgs: fn => {
      const arg = fn[Key.ListToObject]
      return [TypeChecker.isReadonlyArray(arg) ? arg[0] : arg]
    },
    typeChecker: isListToObject,
  }
}
